import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import StatCard from '../components/StatCard';
import { getStats, getJobs } from '../services/adminApi';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, jobsData] = await Promise.all([
          getStats(),
          getJobs()
        ]);
        setStats(statsData);
        setJobs(jobsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400">Laden...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400">
          Fehler: {error}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            Letzte Aktualisierung: {new Date(stats?.timestamp).toLocaleTimeString('de-DE')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/profiles">
            <StatCard
              title="Profile"
              value={stats?.profiles?.total || 0}
              subtitle={`${stats?.profiles?.database_size_kb || 0} KB`}
              icon="👤"
              color="blue"
            />
          </Link>
          <Link to="/admin/messages">
            <StatCard
              title="Messages"
              value={stats?.messages?.total || 0}
              subtitle={`${stats?.messages?.approved || 0} genehmigt`}
              icon="💬"
              color="green"
            />
          </Link>
          <StatCard
            title="Jobs Pending"
            value={stats?.jobs?.pending || 0}
            subtitle={`${stats?.jobs?.in_progress || 0} in Bearbeitung`}
            icon="⏳"
            color="orange"
          />
          <StatCard
            title="Jobs Completed"
            value={stats?.jobs?.completed || 0}
            subtitle="Erfolgreich abgeschlossen"
            icon="✅"
            color="purple"
          />
        </div>

        {/* Recent Jobs */}
        {jobs?.jobs?.length > 0 && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Aktive Jobs</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm">
                    <th className="pb-3 font-medium">ID</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {jobs.jobs.slice(0, 10).map((job) => (
                    <tr key={job.id} className="border-t border-white/5">
                      <td className="py-3 font-mono text-sm">{job.id?.slice(0, 8)}</td>
                      <td className="py-3">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                          {job.type}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          job.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                          job.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' :
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-500">
                        {new Date(job.created_at).toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
